import { getActiveRealtimeBackend } from "./realtime.runtime";

type LegacyNetworkMessage = {
  t?: string;
  lobbyId?: unknown;
  name?: unknown;
  playerName?: unknown;
  primaryWeapon?: unknown;
  joinCode?: unknown;
  timestamp?: unknown;
  aggression?: unknown;
  collection?: unknown;
  selfPreservation?: unknown;
  targetSteamId?: unknown;
  inviteId?: unknown;
};

function isLegacyNetworkMessage(value: unknown): value is LegacyNetworkMessage {
  return typeof value === "object" && value !== null;
}

// biome-ignore lint/complexity/noExcessiveLinesPerFunction: Dispatch table maps legacy message types to realtime backend commands sequentially.
export function dispatchNetworkMessage(message: unknown): boolean {
  if (!isLegacyNetworkMessage(message) || typeof message.t !== "string") {
    return false;
  }

  const realtimeBackend = getActiveRealtimeBackend();
  if (!realtimeBackend) {
    return false;
  }
  if (realtimeBackend && message.t === "deploy_mine") {
    void realtimeBackend.send({ type: "deploy_mine" }).catch((error) => {
      console.error("Failed to dispatch realtime deploy_mine command", error);
    });
    return true;
  }

  if (realtimeBackend && message.t === "leave") {
    void realtimeBackend.send({ type: "leave_lobby" }).catch((error) => {
      console.error("Failed to dispatch realtime leave_lobby command", error);
    });
    return true;
  }

  if (realtimeBackend && message.t === "debug_pellet_boost") {
    void realtimeBackend.send({ type: "debug_pellet_boost" }).catch((error) => {
      console.error(
        "Failed to dispatch realtime debug_pellet_boost command",
        error,
      );
    });
    return true;
  }

  if (realtimeBackend && message.t === "debug_toggle_invincibility") {
    void realtimeBackend
      .send({ type: "debug_toggle_invincibility" })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime debug_toggle_invincibility command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "debug_instant_death") {
    void realtimeBackend
      .send({ type: "debug_instant_death" })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime debug_instant_death command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "debug_instant_victory") {
    void realtimeBackend
      .send({ type: "debug_instant_victory" })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime debug_instant_victory command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "party_create") {
    const playerName =
      typeof message.playerName === "string"
        ? message.playerName
        : typeof message.name === "string"
          ? message.name
          : "";
    void realtimeBackend
      .send({
        type: "party_create",
        playerName,
      })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime party_create command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "party_join_code") {
    const joinCode =
      typeof message.joinCode === "string" ? message.joinCode : undefined;
    const playerName =
      typeof message.playerName === "string"
        ? message.playerName
        : typeof message.name === "string"
          ? message.name
          : "";
    if (!joinCode) {
      return false;
    }
    void realtimeBackend
      .send({
        type: "party_join_code",
        joinCode,
        playerName,
      })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime party_join_code command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "party_toggle_ready") {
    void realtimeBackend.send({ type: "party_toggle_ready" }).catch((error) => {
      console.error(
        "Failed to dispatch realtime party_toggle_ready command",
        error,
      );
    });
    return true;
  }

  if (realtimeBackend && message.t === "party_leave") {
    void realtimeBackend.send({ type: "party_leave" }).catch((error) => {
      console.error("Failed to dispatch realtime party_leave command", error);
    });
    return true;
  }

  if (realtimeBackend && message.t === "party_queue") {
    void realtimeBackend.send({ type: "party_queue" }).catch((error) => {
      console.error("Failed to dispatch realtime party_queue command", error);
    });
    return true;
  }

  if (realtimeBackend && message.t === "party_invite_steam") {
    const targetSteamId =
      typeof message.targetSteamId === "string"
        ? message.targetSteamId
        : undefined;
    if (!targetSteamId) {
      return false;
    }
    void realtimeBackend
      .send({
        type: "party_invite_steam",
        targetSteamId,
      })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime party_invite_steam command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "party_accept_invite") {
    const inviteId =
      typeof message.inviteId === "string" ? message.inviteId : undefined;
    if (!inviteId) {
      return false;
    }
    void realtimeBackend
      .send({
        type: "party_accept_invite",
        inviteId,
      })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime party_accept_invite command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "get_steam_friends") {
    void realtimeBackend.send({ type: "get_steam_friends" }).catch((error) => {
      console.error(
        "Failed to dispatch realtime get_steam_friends command",
        error,
      );
    });
    return true;
  }

  if (realtimeBackend && message.t === "lobby_list_request") {
    void realtimeBackend.send({ type: "request_lobby_list" }).catch((error) => {
      console.error(
        "Failed to dispatch realtime lobby_list_request command",
        error,
      );
    });
    return true;
  }

  if (realtimeBackend && message.t === "ping") {
    const timestamp =
      typeof message.timestamp === "number" ? message.timestamp : Date.now();
    void realtimeBackend.send({ type: "ping", timestamp }).catch((error) => {
      console.error("Failed to dispatch realtime ping command", error);
    });
    return true;
  }

  if (realtimeBackend && message.t === "enable_autoplay") {
    void realtimeBackend
      .send({
        type: "enable_autoplay",
        ...(typeof message.aggression === "number"
          ? { aggression: message.aggression }
          : {}),
        ...(typeof message.collection === "number"
          ? { collection: message.collection }
          : {}),
        ...(typeof message.selfPreservation === "number"
          ? { selfPreservation: message.selfPreservation }
          : {}),
      })
      .catch((error) => {
        console.error(
          "Failed to dispatch realtime enable_autoplay command",
          error,
        );
      });
    return true;
  }

  if (realtimeBackend && message.t === "disable_autoplay") {
    void realtimeBackend.send({ type: "disable_autoplay" }).catch((error) => {
      console.error(
        "Failed to dispatch realtime disable_autoplay command",
        error,
      );
    });
    return true;
  }

  if (realtimeBackend && message.t === "lobby_join_request") {
    const lobbyId =
      typeof message.lobbyId === "string" ? message.lobbyId : undefined;
    const name = typeof message.name === "string" ? message.name : "";
    const primaryWeapon =
      typeof message.primaryWeapon === "string"
        ? message.primaryWeapon
        : undefined;

    if (!lobbyId) {
      return false;
    }

    void realtimeBackend
      .send({
        type: "join_lobby",
        lobbyId,
        playerName: name,
        ...(primaryWeapon ? { primaryWeapon } : {}),
      })
      .catch((error) => {
        console.error("Failed to dispatch realtime join command", error);
      });

    return true;
  }

  return false;
}
